import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy,
  Loader2,
  RefreshCcw,
  Sparkles,
  Users,
  Link as LinkIcon,
  Share2,
  Activity,
  Mail,
  Send,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function slugifyTag(raw) {
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

export default function AdminTesters() {
  const [data, setData] = useState({ items: [], total: 0, active: 0, total_transactions: 0, promo_code: "TESTERS2026", promo_days: 90 });
  const [loading, setLoading] = useState(true);
  const [testerName, setTesterName] = useState("");
  const [sendingRecap, setSendingRecap] = useState(null); // tester id
  const [sendingAll, setSendingAll] = useState(false);

  const baseUrl = useMemo(() => window.location.origin, []);
  const tagSlug = useMemo(() => slugifyTag(testerName.trim()), [testerName]);

  const generatedLink = useMemo(() => {
    const params = new URLSearchParams({ promo: data.promo_code });
    if (tagSlug) params.set("t", tagSlug);
    return `${baseUrl}/register?${params.toString()}`;
  }, [baseUrl, data.promo_code, tagSlug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/testers");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "No se pudo cargar la lista"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Prueba Finly+ Premium gratis",
          text: `Te invito a probar Finly+ Premium gratis por ${data.promo_days} días.`,
          url: generatedLink,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copy(generatedLink, "Link");
    }
  };

  const sendRecap = async (tester) => {
    if (!tester?.email) return;
    if (!window.confirm(`¿Enviar email de Recap a ${tester.email}?`)) return;
    setSendingRecap(tester.id);
    try {
      const { data: res } = await api.post("/admin/trial-recap/send", {
        target_email: tester.email,
        dry_run: false,
        force: true,
      });
      if (res.status === "sent") {
        toast.success(`Recap enviado a ${tester.email}`);
      } else {
        toast.message(`Estado: ${res.status}`, { description: res.reason || "" });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "No se pudo enviar el recap"));
    } finally {
      setSendingRecap(null);
    }
  };

  const sendRecapToAll = async () => {
    if (
      !window.confirm(
        "¿Disparar el job de recap para TODOS los testers elegibles? (Solo los que cumplen las reglas de elegibilidad recibirán email.)",
      )
    )
      return;
    setSendingAll(true);
    try {
      const { data: res } = await api.post("/admin/trial-recap/run-job");
      toast.success(`Procesados: ${res.processed} · Enviados: ${res.sent} · Omitidos: ${res.skipped}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "No se pudo correr el job"));
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-testers-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#F1F5F9] flex items-center gap-3">
              <Users className="w-7 h-7 text-[#FFD54A]" />
              Testers
            </h1>
            <p className="text-sm text-[#94A3B8] mt-1">
              Genera links únicos para tus testers y revisa quién se ha unido.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={sendRecapToAll}
              disabled={sendingAll}
              className="border-[#FFD54A]/40 bg-transparent text-[#FFD54A] hover:bg-[#FFD54A]/10"
              data-testid="admin-testers-send-recap-all"
            >
              {sendingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Disparar recap (todos)
            </Button>
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              className="border-[#2A3447] bg-[#131C2E] text-[#E2E8F0] hover:bg-[#1B2538]"
              data-testid="admin-testers-refresh"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-[#141923] border-[#2A3447] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Total invitados</p>
            <p className="text-3xl font-bold text-[#F1F5F9] mt-2" data-testid="testers-total">
              {data.total}
            </p>
          </Card>
          <Card className="bg-[#141923] border-[#2A3447] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Activos hoy</p>
            <p className="text-3xl font-bold text-[#10B981] mt-2" data-testid="testers-active">
              {data.active}
            </p>
          </Card>
          <Card className="bg-[#141923] border-[#2A3447] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] flex items-center gap-1.5">
              <Activity className="w-3 h-3" />
              Transacciones
            </p>
            <p className="text-3xl font-bold text-[#7AA2FF] mt-2" data-testid="testers-total-transactions">
              {data.total_transactions || 0}
            </p>
            <p className="text-[11px] text-[#94A3B8] mt-1">de todos los testers</p>
          </Card>
          <Card className="bg-[#141923] border-[#2A3447] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Código promo</p>
            <p className="text-2xl font-mono font-bold text-[#FFD54A] mt-2" data-testid="testers-promo-code">
              {data.promo_code}
            </p>
            <p className="text-[11px] text-[#94A3B8] mt-1">{data.promo_days} días Premium</p>
          </Card>
        </div>

        {/* Generator */}
        <Card className="bg-gradient-to-br from-[#141923] to-[#1B2538] border-[#2A3447] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#FFD54A]" />
            <h2 className="text-lg font-semibold text-[#F1F5F9]">Generar link para un tester</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor="tester-name" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Nombre o identificador del tester
              </Label>
              <Input
                id="tester-name"
                value={testerName}
                onChange={(e) => setTesterName(e.target.value)}
                placeholder="Ej. María García"
                maxLength={48}
                className="mt-2 h-11 bg-[#0A0E1A] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#3B82F6]"
                data-testid="admin-testers-name-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => copy(generatedLink, "Link")}
                className="h-11 bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                data-testid="admin-testers-copy-link"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar link
              </Button>
              <Button
                onClick={shareLink}
                variant="outline"
                className="h-11 border-[#2A3447] bg-[#131C2E] text-[#E2E8F0] hover:bg-[#1B2538]"
                data-testid="admin-testers-share-link"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartir
              </Button>
            </div>
          </div>

          <div className="mt-5 rounded-lg bg-[#0A0E1A] border border-[#2A3447] p-4 flex items-start gap-3">
            <LinkIcon className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
            <code
              className="flex-1 text-xs text-[#CBD5E1] break-all font-mono leading-relaxed"
              data-testid="admin-testers-generated-link"
            >
              {generatedLink}
            </code>
          </div>

          {tagSlug && (
            <p className="text-[11px] text-[#94A3B8] mt-3">
              Identificador interno: <span className="font-mono text-[#CBD5E1]">{tagSlug}</span>
            </p>
          )}
        </Card>

        {/* Testers list */}
        <Card className="bg-[#141923] border-[#2A3447] p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-[#2A3447] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#F1F5F9]">Testers registrados</h2>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />}
          </div>
          {data.items.length === 0 && !loading ? (
            <div className="px-6 py-12 text-center text-[#94A3B8] text-sm" data-testid="admin-testers-empty">
              Aún no se ha registrado nadie con el código.
              <br />
              Genera un link arriba y compártelo.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-testers-table">
                <thead className="bg-[#0A0E1A] text-[#64748B] uppercase text-[11px] tracking-[0.15em]">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium">Tester</th>
                    <th className="text-left px-4 py-3 font-medium">Identificador</th>
                    <th className="text-left px-4 py-3 font-medium">Registrado</th>
                    <th className="text-left px-4 py-3 font-medium">Último acceso</th>
                    <th className="text-left px-4 py-3 font-medium">Transacciones</th>
                    <th className="text-left px-4 py-3 font-medium">Días restantes</th>
                    <th className="text-right px-4 py-3 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A3447]">
                  {data.items.map((t) => (
                    <tr key={t.id} className="hover:bg-[#1B2538] transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#F1F5F9]">{t.name || "—"}</div>
                        <div className="text-[11px] text-[#94A3B8]">{t.email}</div>
                      </td>
                      <td className="px-4 py-4 text-[#CBD5E1] font-mono text-xs">
                        {t.tester_tag || <span className="text-[#64748B]">—</span>}
                      </td>
                      <td className="px-4 py-4 text-[#94A3B8] text-xs">{formatDate(t.created_at)}</td>
                      <td className="px-4 py-4 text-[#94A3B8] text-xs">{formatDate(t.last_login_at)}</td>
                      <td className="px-4 py-4" data-testid={`tester-tx-count-${t.id}`}>
                        {t.transaction_count > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#0052FF]/10 border border-[#0052FF]/30 text-[#7AA2FF] text-xs font-semibold">
                            <Activity className="w-3 h-3" />
                            {t.transaction_count}
                          </span>
                        ) : (
                          <span className="text-[#64748B] text-xs">0</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {t.active ? (
                          <Badge className="bg-[#10B981]/15 text-[#34D399] border-[#10B981]/30 hover:bg-[#10B981]/15">
                            {t.days_left} días
                          </Badge>
                        ) : (
                          <Badge className="bg-[#64748B]/15 text-[#94A3B8] border-[#64748B]/30 hover:bg-[#64748B]/15">
                            Expirado
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => sendRecap(t)}
                          disabled={sendingRecap === t.id}
                          className="h-8 px-3 text-[#FFD54A] hover:bg-[#FFD54A]/10 hover:text-[#FFD54A]"
                          data-testid={`tester-send-recap-${t.id}`}
                        >
                          {sendingRecap === t.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Recap
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
